/**
 * FilterBar Component
 *
 * Main smart input component that combines filter pills, suggestions, and text input
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { GraphDataNode } from '../../types/graph.types';
import {
  FilterBarProps,
  FilterDefinition,
  ActiveFilter,
  FilterSuggestion,
} from '../types';
import { FilterPill } from './FilterPill';
import { SuggestionDropdown } from './SuggestionDropdown';
import { FilterConfigDialog } from './FilterConfigDialog';
import { useFilterSuggestions } from '../hooks/useFilterSuggestions';
import { allFilterDefinitions } from '../definitions';

/**
 * Generate a unique ID for filter instances
 */
function generateFilterId(): string {
  return `filter-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * FilterBar Component Props (extended)
 */
interface FilterBarComponentProps extends FilterBarProps {
  /** All nodes for generating suggestions */
  nodes: GraphDataNode[];
  /** Custom filter definitions (optional, uses all by default) */
  definitions?: Record<string, FilterDefinition>;
}

/**
 * FilterBar Component
 */
export const FilterBar: React.FC<FilterBarComponentProps> = ({
  filterSet,
  onFilterSetChange,
  availableFilters,
  placeholder = 'Search or filter...',
  className = '',
  lockedFilters = [],
  autoFocus = false,
  nodes,
  definitions = allFilterDefinitions,
}) => {
  // State
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [editingFilter, setEditingFilter] = useState<ActiveFilter | null>(null);
  const [isNewFilter, setIsNewFilter] = useState(false);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter available definitions
  const filteredDefinitions = useMemo(() => {
    if (!availableFilters || availableFilters.length === 0) {
      return definitions;
    }
    return Object.fromEntries(
      Object.entries(definitions).filter(([id]) => availableFilters.includes(id))
    );
  }, [definitions, availableFilters]);

  // Get suggestions
  const { suggestions } = useFilterSuggestions(
    inputValue,
    nodes,
    filteredDefinitions,
    { maxSuggestions: 8 }
  );

  // Combine locked and active filters for display
  const allDisplayedFilters = useMemo(
    () => [...lockedFilters, ...filterSet],
    [lockedFilters, filterSet]
  );

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [suggestions]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  /**
   * Add a filter from suggestion
   */
  const addFilterFromSuggestion = useCallback(
    (suggestion: FilterSuggestion) => {
      if (suggestion.needsConfiguration) {
        // Create a temporary filter and open config dialog
        const tempFilter: ActiveFilter = {
          id: generateFilterId(),
          filterId: suggestion.filterId,
          operator: suggestion.prefilledOperator,
          value: suggestion.prefilledValue,
        };
        setEditingFilter(tempFilter);
        setIsNewFilter(true);
      } else {
        // Add directly
        const newFilter: ActiveFilter = {
          id: generateFilterId(),
          filterId: suggestion.filterId,
          operator: suggestion.prefilledOperator,
          value: suggestion.prefilledValue,
        };
        onFilterSetChange([...filterSet, newFilter]);
      }

      setInputValue('');
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    },
    [filterSet, onFilterSetChange]
  );

  /**
   * Remove a filter by ID
   */
  const removeFilter = useCallback(
    (filterId: string) => {
      onFilterSetChange(filterSet.filter((f) => f.id !== filterId));
      inputRef.current?.focus();
    },
    [filterSet, onFilterSetChange]
  );

  /**
   * Open filter for editing
   */
  const editFilter = useCallback((filter: ActiveFilter) => {
    setEditingFilter(filter);
    setIsNewFilter(false);
  }, []);

  /**
   * Apply changes from config dialog
   */
  const applyFilterChange = useCallback(
    (updatedFilter: ActiveFilter) => {
      if (isNewFilter) {
        // Add new filter
        onFilterSetChange([...filterSet, updatedFilter]);
      } else {
        // Update existing filter
        onFilterSetChange(
          filterSet.map((f) => (f.id === updatedFilter.id ? updatedFilter : f))
        );
      }
      setEditingFilter(null);
      setIsNewFilter(false);
      inputRef.current?.focus();
    },
    [isNewFilter, filterSet, onFilterSetChange]
  );

  /**
   * Cancel config dialog
   */
  const cancelConfig = useCallback(() => {
    setEditingFilter(null);
    setIsNewFilter(false);
    inputRef.current?.focus();
  }, []);

  /**
   * Clear all filters
   */
  const clearAllFilters = useCallback(() => {
    onFilterSetChange([]);
    setInputValue('');
    inputRef.current?.focus();
  }, [onFilterSetChange]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isDropdownOpen || suggestions.length === 0) {
        // Handle backspace to remove last filter
        if (
          e.key === 'Backspace' &&
          inputValue === '' &&
          filterSet.length > 0
        ) {
          e.preventDefault();
          const lastFilter = filterSet[filterSet.length - 1];
          removeFilter(lastFilter.id);
        }

        // Open dropdown on any key if not open
        if (e.key !== 'Backspace' && e.key !== 'Escape' && inputValue.length > 0) {
          setIsDropdownOpen(true);
        }

        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;

        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;

        case 'Enter':
          e.preventDefault();
          if (suggestions[highlightedIndex]) {
            addFilterFromSuggestion(suggestions[highlightedIndex]);
          }
          break;

        case 'Tab':
          if (suggestions[highlightedIndex]) {
            e.preventDefault();
            addFilterFromSuggestion(suggestions[highlightedIndex]);
          }
          break;

        case 'Escape':
          e.preventDefault();
          setIsDropdownOpen(false);
          break;
      }
    },
    [
      isDropdownOpen,
      suggestions,
      inputValue,
      filterSet,
      highlightedIndex,
      addFilterFromSuggestion,
      removeFilter,
    ]
  );

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      // Always show dropdown on input change (empty query shows filter categories)
      setIsDropdownOpen(true);
    },
    []
  );

  /**
   * Handle input focus - always show dropdown (shows filter categories when empty)
   */
  const handleInputFocus = useCallback(() => {
    setIsDropdownOpen(true);
  }, []);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Main input area */}
      <div
        className="
          flex flex-wrap items-center gap-2
          min-h-[44px] px-3 py-2
          bg-bg-2/70 backdrop-blur-sm
          border border-light
          rounded-xl
          focus-within:ring-2 focus-within:ring-accent-gold/40 focus-within:border-accent-gold/40
          transition-shadow
        "
        onClick={() => inputRef.current?.focus()}
      >
        {/* Search icon */}
        <MagnifyingGlassIcon className="h-5 w-5 text-text-tertiary flex-shrink-0" />

        {/* Filter pills */}
        {allDisplayedFilters.map((filter) => {
          const definition = definitions[filter.filterId];
          if (!definition) return null;

          const isLocked = lockedFilters.some((lf) => lf.id === filter.id);

          return (
            <FilterPill
              key={filter.id}
              filter={filter}
              definition={definition}
              locked={isLocked}
              onRemove={isLocked ? undefined : () => removeFilter(filter.id)}
              onClick={isLocked ? undefined : () => editFilter(filter)}
            />
          );
        })}

        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          placeholder={allDisplayedFilters.length === 0 ? placeholder : ''}
          className="
            flex-1 min-w-[120px]
            bg-transparent
            border-none outline-none
            text-text-primary
            placeholder-text-tertiary
            text-sm
          "
          aria-label="Filter search"
          aria-expanded={isDropdownOpen}
          aria-autocomplete="list"
          aria-controls="filter-suggestions"
          role="combobox"
        />

        {/* Clear all button */}
        {(filterSet.length > 0 || inputValue.length > 0) && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="
              p-1 rounded-full
              text-text-tertiary hover:text-text-secondary
              hover:bg-bg-3/60
              transition-colors
            "
            aria-label="Clear all filters"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      <SuggestionDropdown
        suggestions={suggestions}
        definitions={definitions}
        highlightedIndex={highlightedIndex}
        onSelect={addFilterFromSuggestion}
        isVisible={isDropdownOpen && suggestions.length > 0}
        query={inputValue}
        onHighlightChange={setHighlightedIndex}
      />

      {/* Config dialog */}
      {editingFilter && (
        <FilterConfigDialog
          filter={editingFilter}
          definition={definitions[editingFilter.filterId] || null}
          isNew={isNewFilter}
          onApply={applyFilterChange}
          onCancel={cancelConfig}
          nodes={nodes}
        />
      )}
    </div>
  );
};

export default FilterBar;
